// Configuration des roles labo utilisee pour le gating visuel.

import { CONSTANTS } from '@/types/enum/constants'
import type { IndustryRoleConfig, IndustryRoleKey } from '@/types/industry/industryTypes'

// Roles API "Espace Industry" (cf config/packages/security.yaml cote @group-api).
// Hierarchie effective : ADMINISTRATEUR (cle historique 'siege') >= SIEGE (fonction, cle
// 'headOffice') >= DR >= DP. On resout du plus fort au plus faible car par heritage un role
// possede aussi les roles inferieurs.
const INDUSTRY_ROLE_API = {
  SIEGE: 'ROLE_INDUSTRY_USER_ADMINISTRATOR',
  HEAD_OFFICE: 'ROLE_INDUSTRY_HEAD_OFFICE',
  DR: 'ROLE_INDUSTRY_DR',
  DP: 'ROLE_INDUSTRY_DP',
  // Administration des modules du labo (herite par SIEGE cote API).
  MODULE_ADMIN: 'ROLE_INDUSTRY_MODULE_ADMIN',
} as const

/** Mapping role API -> role labo front, ordonne du plus fort au plus faible. */
const INDUSTRY_ROLE_RESOLUTION: ReadonlyArray<[string, IndustryRoleKey]> = [
  [INDUSTRY_ROLE_API.SIEGE, 'siege'],
  [INDUSTRY_ROLE_API.HEAD_OFFICE, 'headOffice'],
  [INDUSTRY_ROLE_API.DR, 'dr'],
  [INDUSTRY_ROLE_API.DP, 'dp'],
]

/** Roles pharmacien (hors hierarchie labo) qui derivent le role front 'pharmacie'. */
const INDUSTRY_PHARMACY_ROLES: ReadonlyArray<string> = [
  CONSTANTS.ROLE_PHARMACIEN,
  CONSTANTS.ROLE_COTITULAIRE_PHARMACIEN,
  CONSTANTS.ROLE_DELEGATE_PHARMACIEN_CO_TITU,
]

/**
 * Extrait de maniere robuste la liste des roles utilisateur depuis un profil,
 * quelle que soit sa forme (dataMyProfile, rolesProfile, session...).
 */
const extractRoles = (dataMyProfile?: unknown): string[] => {
  if (!dataMyProfile || typeof dataMyProfile !== 'object') return []
  const p = dataMyProfile as Record<string, any>
  const candidate =
    p.roles ??
    p.loggedUserRoles ??
    p.rolesProfile?.loggedUserRoles ??
    p.user?.roles ??
    p.switchUserRoles
  return Array.isArray(candidate) ? candidate.filter((r): r is string => typeof r === 'string') : []
}

export const INDUSTRY_ROLES: Record<IndustryRoleKey, IndustryRoleConfig> = {
  siege: {
    key: 'siege',
    scope: 'industry.roleScope.siege',
    nav: [
      'dashboard',
      'campagnes',
      'finance',
      'invoices',
      'pharmacies',
      'wishes',
      'plvRequests',
      'users',
      'zones',
      'stock',
      'docs',
      'settings',
      'guide',
    ],
    // Le Siege peut ecrire (cf security back CompanyZone : ROLE_INDUSTRY_USER_ADMINISTRATOR en POST/PATCH/DELETE,
    // scope par company_owner). DR/DP n'heritent pas de SIEGE -> read-only.
    caps: { read: true, extract: true, write: true, campaignExport: true },
  },
  // Fonction « Siège » (22/10/2026) : la vue de l'administrateur, tout le parc, SANS
  // « Utilisateurs & rôles » ni les zones (`users`, `zones` absents : menu et accès direct par
  // URL fermés, bloc Zones des Paramètres masqué), et SANS ecriture (`write: false`) : la mise à
  // jour en masse des pharmacies est réservée à l'administrateur. Les gates d'écriture propres
  // à l'administrateur restent côté API sur ROLE_INDUSTRY_USER_ADMINISTRATOR.
  headOffice: {
    key: 'headOffice',
    scope: 'industry.roleScope.headOffice',
    nav: [
      'dashboard',
      'campagnes',
      'finance',
      'invoices',
      'pharmacies',
      'wishes',
      'plvRequests',
      'stock',
      'docs',
      'settings',
      'guide',
    ],
    caps: { read: true, extract: true, write: false, campaignExport: true },
  },
  // DR et delegue : MEME interface (decision Mathieu, 28/09/2026) — tableau de bord, campagnes,
  // pharmacies, en lecture seule, et le guide de l'espace (ouvert depuis le tableau de bord). Seul le perimetre differe (region / secteur), et il est
  // applique par le serveur. Ni documents, ni parametres, ni suivi financier, ni stock : ce
  // sont des ecrans de pilotage du Siege.
  dr: {
    key: 'dr',
    scope: 'industry.roleScope.dr',
    nav: ['dashboard', 'campagnes', 'pharmacies', 'guide'],
    caps: { read: true, extract: false, write: false, campaignExport: true },
  },
  dp: {
    key: 'dp',
    scope: 'industry.roleScope.dp',
    nav: ['dashboard', 'campagnes', 'pharmacies', 'guide'],
    caps: { read: true, extract: false, write: false, campaignExport: true },
  },
  pharmacie: {
    key: 'pharmacie',
    scope: 'industry.roleScope.pharmacie',
    nav: ['dashboard', 'campagnes', 'docs', 'settings'],
    caps: { read: true, extract: true, write: true, campaignExport: false },
  },
  groupement: {
    key: 'groupement',
    scope: 'industry.roleScope.groupement',
    nav: ['dashboard', 'campagnes', 'pharmacies', 'docs'],
    caps: { read: true, extract: false, write: false, campaignExport: false },
  },
}

/**
 * Administrateur OU fonction « Siège » : les profils qui pilotent tout le parc du laboratoire
 * (exports complets, arbitrage radio). Seuls les utilisateurs, les zones, la mise à
 * jour en masse et les modules distinguent l'administrateur.
 */
export const isIndustryHeadOfficeOrAdmin = (role: IndustryRoleKey): boolean =>
  role === 'siege' || role === 'headOffice'

/**
 * Resout le role labo (siege/headOffice/dr/dp/pharmacie) a partir des VRAIS roles utilisateur.
 *
 * Ordre de resolution : du plus fort au plus faible (SIEGE > DR > DP), car par
 * hierarchie API un siege possede aussi les roles inferieurs. Le role 'pharmacie'
 * derive des roles pharmacien existants (hors hierarchie labo).
 *
 * Fallback 'dp' (fail-closed) : en l'absence de role connu on retombe sur le role le
 * plus FAIBLE (lecture seule, scope minimal), jamais 'siege' — un profil ambigu ne doit
 * pas obtenir les droits d'ecriture et le perimetre national du siege.
 *
 */
export const resolveIndustryRole = (dataMyProfile?: unknown): IndustryRoleKey => {
  const roles = extractRoles(dataMyProfile)
  if (roles.length === 0) return 'dp'

  for (const [apiRole, labRole] of INDUSTRY_ROLE_RESOLUTION) {
    if (roles.includes(apiRole)) return labRole
  }

  if (roles.some(role => INDUSTRY_PHARMACY_ROLES.includes(role))) return 'pharmacie'

  return 'dp'
}

/**
 * Roles d'un compte de TERRAIN (DR, delegue) tel que le Siege le cree : la hierarchie labo,
 * l'acces a l'espace et le socle. Tout autre role (module ouvert, pharmacien, admin...) donne
 * a l'utilisateur quelque chose a faire dans l'espace Pharmedigroup.
 */
const INDUSTRY_FIELD_ONLY_ROLES: ReadonlyArray<string> = [
  INDUSTRY_ROLE_API.DR,
  INDUSTRY_ROLE_API.DP,
  CONSTANTS.ROLE_DYNAMIC_ROUTE_INDUSTRY_USER_ENTITY_GROUP,
  'ROLE_USER',
]

/**
 * DR ou delegue SANS aucun autre role : il n'a rien dans l'espace Pharmedigroup (03/10/2026),
 * le selecteur d'espace ne le lui propose donc pas. Le Siege garde les deux espaces, comme un
 * DR ou un delegue a qui un module a ete ouvert.
 */
export const isIndustryFieldOnlyUser = (dataMyProfile?: unknown): boolean => {
  const roles = extractRoles(dataMyProfile)
  return (
    (roles.includes(INDUSTRY_ROLE_API.DR) || roles.includes(INDUSTRY_ROLE_API.DP)) &&
    roles.every(role => INDUSTRY_FIELD_ONLY_ROLES.includes(role))
  )
}

/**
 * Indique si l'utilisateur peut administrer les modules du labo (activation self-service
 * + attribution des acces aux users). Vrai si le profil porte ROLE_INDUSTRY_MODULE_ADMIN
 * (droit dedie) OU ROLE_INDUSTRY_USER_ADMINISTRATOR (qui en herite cote API). Fail-closed : tout autre
 * profil (DR/DP/pharmacie) renvoie false.
 */
export const canAdministrateIndustryModules = (dataMyProfile?: unknown): boolean => {
  const roles = extractRoles(dataMyProfile)
  return (
    roles.includes(INDUSTRY_ROLE_API.MODULE_ADMIN) || roles.includes(INDUSTRY_ROLE_API.SIEGE)
  )
}
